import React, { useState } from "react"

import DatetimeRenderer from "../../../Material/DatetimeRenderer"
import EditDocumentTypePopover from "./EditDocumentTypePopover"
import EditNotesPopover from "./EditNotesPopover"

const OWNER_BADGE = {
    PARTY: "idm-badge-party",
    LIABILITY: "idm-badge-liability",
    SUBROGATION: "idm-badge-subrogation",
}

/** The slug encodes which party or claim a document hangs off, e.g. "PARTY_<globalid>". */
export function ownerKindFromSlug(slug) {
    if (!slug) return null
    const kind = String(slug).split("_")[0].toUpperCase()
    return OWNER_BADGE[kind] ? kind : null
}

export const OwnerBadge = ({ slug }) => {
    const kind = ownerKindFromSlug(slug)
    if (!kind) return null
    return <span className={`idm-badge ${OWNER_BADGE[kind]}`}>{kind}</span>
}

const DOCUMENT_CATEGORIES = ["INCIDENT", "PARTY", "CAR", "LIABILITY", "SUBROGATION"]

/** The slug's prefix is also the document type category, e.g. "PARTY_<id>" -> "PARTY". */
export function categoryFromSlug(slug) {
    const kind = String(slug || "").split("_")[0].toUpperCase()
    return DOCUMENT_CATEGORIES.includes(kind) ? kind : "INCIDENT"
}

export const InfoRow = ({ label, children, action }) => (
    <div className="idm-info-row">
        <span className="idm-info-label">{label}</span>
        <span className="idm-info-value">{children}</span>
        {action}
    </div>
)

const EditChip = ({ id, label, onClick }) => (
    <button type="button" className="idm-edit-chip" id={id} title={label} aria-label={label} onClick={onClick}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17z" />
            <path d="M13.5 6.5l4 4" />
        </svg>
    </button>
)

/**
 * Notes are edited from here rather than the modal footer: the affordance belongs next
 * to the value it changes, and the footer is on screen even when the notes are not.
 */
export const DocumentDetailsPanel = ({ document, onSaved }) => {
    const [editingNotes, setEditingNotes] = useState(false)
    const [editingType, setEditingType] = useState(false)
    const editTargetId = `idm-edit-notes-${document.pk}`
    const typeTargetId = `idm-edit-type-${document.pk}`

    return (
    <div className="idm-panel-body idm-info-list">
        <InfoRow
            label="Document type"
            action={<EditChip id={typeTargetId} label="Edit document type" onClick={() => setEditingType(true)} />}
        >
            {document.documentType ? document.documentType.name : "--"}
        </InfoRow>
        <InfoRow label="Linked to"><OwnerBadge slug={document.slug} /> {!document.slug && "--"}</InfoRow>
        <InfoRow
            label="Notes"
            action={<EditChip id={editTargetId} label="Edit notes" onClick={() => setEditingNotes(true)} />}
        >
            {document.notes || "--"}
        </InfoRow>
        <InfoRow label="Uploaded">
            {document.dateAdded ? <DatetimeRenderer datetime={document.dateAdded} /> : "--"}
        </InfoRow>
        <InfoRow label="Uploaded by">{document.createdBy ? document.createdBy.username : "--"}</InfoRow>
        <InfoRow label="File">{document.document || "--"}</InfoRow>
        <InfoRow label="Status">
            <span className={`idm-badge ${document.isArchived ? "idm-badge-archived" : "idm-badge-active"}`}>
                {document.isArchived ? "ARCHIVED" : "ACTIVE"}
            </span>
        </InfoRow>
        {editingType && (
            <EditDocumentTypePopover
                open
                target={typeTargetId}
                documentId={document.id}
                category={categoryFromSlug(document.slug)}
                currentTypeId={document.documentType ? document.documentType.id : ""}
                handleClose={() => setEditingType(false)}
                onSaved={onSaved}
            />
        )}
        {editingNotes && (
            <EditNotesPopover
                open
                target={editTargetId}
                documentId={document.id}
                currentNotes={document.notes || ""}
                handleClose={() => setEditingNotes(false)}
                onSaved={onSaved}
            />
        )}
    </div>
    )
}
